/**
 * Cbutton Component
 * Composed interactive button using Ccontainer + Ctext + optional Cicon
 */
"use client";
import {
  useMemo,
  type AriaRole,
  type CSSProperties,
  type KeyboardEvent,
  type MouseEvent,
  type PointerEvent,
  type ReactElement,
} from "react";
import { getColorValue } from "@/theme/colors";
import { getRadiusValue, RadiusSize } from "@/theme/radius";
import { Ccontainer, type CcontainerProps } from "../Ccontainer";
import { handleButtonActivationKeyDown } from "./buttonInteraction";
import {
  resolveButtonRootAttributes,
  type ResolvedButtonRootAttributes,
} from "./buttonRootAttributes";
import { CBUTTON_SURFACE_ROOT_ATTRIBUTES } from "./cbuttonSurfaceMarker";
import { CbuttonHrefTarget, type CbuttonProps } from "./buttonTypes";
import { useButtonRenderModel } from "./useButtonRenderModel";
export { BUTTON_ICON_POSITION, CbuttonHrefTarget } from "./buttonTypes";
export type { ButtonIconPosition, CbuttonProps } from "./buttonTypes";

/**
 * A new tab must not hand the destination a window handle or a referrer; the
 * anchor also has to shed the user-agent link paint, because every colour in a
 * Cbutton comes from its preset.
 */
const ANCHOR_ROOT_REL = "noreferrer";
const ANCHOR_ROOT_STYLE: CSSProperties = {
  display: "inline-flex",
  color: "inherit",
  textDecoration: "none",
};

/**
 * The role a keyboard-activatable root announces. A root that says it is a
 * button and takes focus has to answer Enter and Space, whoever declared those
 * two things — Cbutton from its own semantics, or a composite caller that owns
 * the outer semantics itself.
 */
const BUTTON_ROOT_ROLE: AriaRole = "button";
const FOCUSABLE_TAB_INDEX = 0;

/**
 * The surface's visual defaults. Overridable per call site, never by `style` —
 * these reach the `Ccontainer` as props, so each one is a `CbuttonProps` field
 * defaulted to the literal Cbutton has always passed. See SPEC §3.1.
 */
const DEFAULT_ALIGN_ITEMS: CbuttonProps["alignItems"] = "center";
const DEFAULT_JUSTIFY_CONTENT: CbuttonProps["justifyContent"] = "center";
const DEFAULT_INNER_BORDER = false;
/** The surface's own paint transition. Reachable by `containerStyle.transition`. */
const SURFACE_SMOOTH = true;
const SURFACE_ANIMATION_DURATION = 200;
const SURFACE_DISPLAY: CcontainerProps["display"] = "flex";

function resolveInlineRadius(
  radius: CbuttonProps["radius"],
): number | undefined {
  if (radius == null) {
    return undefined;
  }
  return typeof radius === "number"
    ? radius
    : getRadiusValue(radius as RadiusSize);
}

interface InlineSurfaceStyleInput {
  readonly model: ReturnType<typeof useButtonRenderModel>;
  readonly alignItems: CbuttonProps["alignItems"];
  readonly justifyContent: CbuttonProps["justifyContent"];
  /**
   * The inline root has no separate content wrapper — this one span is both the
   * surface and the content layer — so `contentStyle` lands here, after
   * `containerStyle`, mirroring the order `Ccontainer` gives the two on the
   * non-inline roots. Dropping it was silent: an inline caller's `contentStyle`
   * reached nothing at all.
   */
  readonly contentStyle: CbuttonProps["contentStyle"];
}

function resolveInlineSurfaceStyle({
  model,
  alignItems,
  contentStyle,
  justifyContent,
}: InlineSurfaceStyleInput): CSSProperties {
  const visual = model.containerVisualProps;
  const borderWidth = visual.borderWidth ?? 0;
  const borderColor = visual.borderColor
    ? getColorValue(visual.borderColor, visual.borderOpacity)
    : undefined;
  return {
    display: "inline-flex",
    alignItems,
    justifyContent,
    gap: model.gap,
    padding: model.buttonPadding,
    width: model.buttonSizeProps.width,
    height: model.buttonSizeProps.height,
    borderRadius: resolveInlineRadius(model.radius),
    backgroundColor: visual.backgroundColor
      ? getColorValue(visual.backgroundColor, visual.backgroundOpacity)
      : undefined,
    border:
      borderWidth > 0 ? `${borderWidth}px solid ${borderColor}` : undefined,
    backdropFilter:
      visual.backdropBlur && visual.backdropBlur > 0
        ? `blur(${visual.backdropBlur}px)`
        : undefined,
    boxShadow: visual.boxShadow,
    transition: `all ${String(SURFACE_ANIMATION_DURATION)}ms ease`,
    ...model.resolvedContainerStyle,
    ...contentStyle,
  };
}
export function Cbutton(props: CbuttonProps): ReactElement {
  const model = useButtonRenderModel({ ...props });
  // Every prop the three roots read, taken once. React's refs rule treats a
  // member read on a props object that also yields a `ref` as a ref access
  // during render, so all three branches work from locals rather than from
  // `props.x` inside the JSX.
  const {
    alignItems = DEFAULT_ALIGN_ITEMS,
    ariaDisabled,
    ariaPressed,
    composite,
    contentStyle,
    disableHoverEffects,
    glowLayer,
    href,
    hrefTarget,
    inline,
    innerBorder = DEFAULT_INNER_BORDER,
    anchorRootRef,
    inlineRootRef,
    justifyContent = DEFAULT_JUSTIFY_CONTENT,
    onKeyDown,
    onPointerDown,
    onPointerEnter,
    onPointerLeave,
    rootAttributes,
    rootClassName,
    rootRef,
    rootRole,
    rootTabIndex,
    style: rootStyle,
    title,
  } = props;
  const hasButtonSemantics = model.hasButtonSemantics && !composite;
  const resolvedRootRole = rootRole ?? (hasButtonSemantics ? BUTTON_ROOT_ROLE : undefined);
  const resolvedRootTabIndex =
    rootTabIndex ?? (hasButtonSemantics ? FOCUSABLE_TAB_INDEX : undefined);
  /**
   * `composite` exists to stop Cbutton nesting a button role inside another
   * one — it is not a statement that the root has no keyboard model. A
   * composite caller that declares `rootRole="button"` and a focusable
   * `rootTabIndex` HAS built a button, and a focusable div announcing as a
   * button with dead Enter and Space keys is the defect that produces. So the
   * activation follows the semantics that actually reach the DOM, whoever put
   * them there.
   */
  const activatesOnKeyboard =
    hasButtonSemantics ||
    (model.isInteractive &&
      href == null &&
      resolvedRootRole === BUTTON_ROOT_ROLE &&
      resolvedRootTabIndex != null &&
      resolvedRootTabIndex >= FOCUSABLE_TAB_INDEX);
  /**
   * One bag, one write order, for all three roots. The anchor branch resolves to
   * the same role and tab index it used to write raw, because `href` forces
   * `hasButtonSemantics` false and the resolved values then fall back to exactly
   * the caller's `rootRole` / `rootTabIndex`.
   */
  const semanticRootAttributes: ResolvedButtonRootAttributes =
    resolveButtonRootAttributes({
      rootAttributes,
      rootClassName,
      title,
      role: resolvedRootRole,
      tabIndex: resolvedRootTabIndex,
      ariaLabel: model.ariaLabel,
      ariaPressed,
      ariaDisabled,
    });
  const beforeContent = model.beforeContent;
  const buttonContent = model.buttonContent;
  const containerPreset = model.containerPreset;
  const containerRadius = model.radius;
  const containerVisualProps = model.containerVisualProps;
  const containerGlossy = model.glossy;
  const buttonPadding = model.buttonPadding;
  const buttonWidth = model.buttonSizeProps.width;
  const buttonHeight = model.buttonSizeProps.height;
  const buttonGap = model.gap;
  const clickAnimation = model.clickAnimation;
  const preserveStyle = model.preserveStyle;
  const isLoading = model.isLoading;
  const containerOnClick = model.clickInvestigationHandlers.containerOnClick;
  const className = model.className;
  const resolvedContainerStyle = model.resolvedContainerStyle;
  const containerElement = useMemo<ReactElement>(
    (): ReactElement => (
      <Ccontainer
        rootAttributes={CBUTTON_SURFACE_ROOT_ATTRIBUTES}
        preset={containerPreset}
        radius={containerRadius}
        {...containerVisualProps}
        glossy={containerGlossy}
        glowLayer={glowLayer}
        innerBorder={innerBorder}
        padding={buttonPadding}
        width={buttonWidth}
        height={buttonHeight}
        display={SURFACE_DISPLAY}
        alignItems={alignItems}
        justifyContent={justifyContent}
        gap={buttonGap}
        clickAnimation={clickAnimation}
        preserveStyle={preserveStyle}
        smooth={SURFACE_SMOOTH}
        animationDuration={SURFACE_ANIMATION_DURATION}
        loading={isLoading}
        onClick={containerOnClick}
        className={className}
        style={resolvedContainerStyle}
        contentWrapperStyle={contentStyle}
      >
        {beforeContent}
        {buttonContent}
      </Ccontainer>
    ),
    [
      alignItems,
      beforeContent,
      buttonContent,
      buttonGap,
      buttonHeight,
      buttonPadding,
      buttonWidth,
      className,
      clickAnimation,
      containerGlossy,
      containerOnClick,
      containerPreset,
      containerRadius,
      containerVisualProps,
      glowLayer,
      innerBorder,
      isLoading,
      justifyContent,
      preserveStyle,
      resolvedContainerStyle,
      contentStyle,
    ],
  );
  if (inline) {
    return (
      <span
        {...semanticRootAttributes}
        ref={inlineRootRef}
        onKeyDown={(event) => {
          onKeyDown?.(event as unknown as KeyboardEvent<HTMLDivElement>);
          if (event.defaultPrevented) {
            return;
          }
          handleButtonActivationKeyDown({
            event: event as unknown as KeyboardEvent<HTMLDivElement>,
            onActivate: activatesOnKeyboard
              ? model.clickInvestigationHandlers.containerOnClick
              : undefined,
          });
        }}
        style={{
          ...model.buttonStyle,
          cursor:
            model.isInteractive && !disableHoverEffects
              ? "pointer"
              : "default",
        }}
        onTouchStart={model.pressState.handleTouchStart}
        onTouchEnd={model.pressState.handleTouchEnd}
        onTouchCancel={model.pressState.handleTouchCancel}
        onMouseEnter={model.pressState.handleMouseEnter}
        onMouseLeave={model.pressState.handleMouseLeave}
        onMouseDown={model.pressState.handleMouseDown}
        onMouseUp={model.pressState.handleMouseUp}
        onClick={(event) =>
          model.clickInvestigationHandlers.containerOnClick?.(
            event as unknown as MouseEvent<HTMLDivElement>,
          )
        }
        onClickCapture={() =>
          model.clickInvestigationHandlers.handleClickInvestigationCapture()
        }
        onPointerDown={(event) =>
          onPointerDown?.(
            event as unknown as PointerEvent<HTMLDivElement>,
          )
        }
        onPointerEnter={(event) =>
          onPointerEnter?.(
            event as unknown as PointerEvent<HTMLDivElement>,
          )
        }
        onPointerLeave={(event) =>
          onPointerLeave?.(
            event as unknown as PointerEvent<HTMLDivElement>,
          )
        }
        onPointerDownCapture={() =>
          model.clickInvestigationHandlers.handlePointerDownInvestigationCapture()
        }
      >
        <span
          className={model.className}
          style={resolveInlineSurfaceStyle({
            alignItems,
            contentStyle,
            justifyContent,
            model,
          })}
        >
          {model.beforeContent}
          {model.buttonContent}
        </span>
      </span>
    );
  }
  if (href != null) {
    return (
      <a
        {...semanticRootAttributes}
        ref={anchorRootRef}
        href={href}
        target={hrefTarget ?? CbuttonHrefTarget.Blank}
        rel={ANCHOR_ROOT_REL}
        onKeyDown={(event) => {
          onKeyDown?.(event as unknown as KeyboardEvent<HTMLDivElement>);
        }}
        style={{ ...ANCHOR_ROOT_STYLE, ...model.buttonStyle }}
        onTouchStart={model.pressState.handleTouchStart}
        onTouchEnd={model.pressState.handleTouchEnd}
        onTouchCancel={model.pressState.handleTouchCancel}
        onMouseEnter={model.pressState.handleMouseEnter}
        onMouseLeave={model.pressState.handleMouseLeave}
        onMouseDown={model.pressState.handleMouseDown}
        onMouseUp={model.pressState.handleMouseUp}
        onClickCapture={
          model.clickInvestigationHandlers.handleClickInvestigationCapture
        }
        onClick={(event) => {
          // Keyboard Enter and a click on the root's own padding both land
          // here; a click inside the surface is already the Ccontainer's, and
          // this guard is what keeps it from firing twice.
          if (event.target === event.currentTarget) {
            model.clickInvestigationHandlers.containerOnClick?.(
              event as unknown as MouseEvent<HTMLDivElement>,
            );
          }
        }}
        onPointerDown={(event) =>
          onPointerDown?.(event as unknown as PointerEvent<HTMLDivElement>)
        }
        onPointerEnter={(event) =>
          onPointerEnter?.(event as unknown as PointerEvent<HTMLDivElement>)
        }
        onPointerLeave={(event) =>
          onPointerLeave?.(event as unknown as PointerEvent<HTMLDivElement>)
        }
        onPointerDownCapture={
          model.clickInvestigationHandlers.handlePointerDownInvestigationCapture
        }
      >
        {containerElement}
      </a>
    );
  }
  return (
    <div
      {...semanticRootAttributes}
      ref={rootRef}
      onKeyDown={(event) => {
        onKeyDown?.(event);
        if (event.defaultPrevented) {
          return;
        }
        handleButtonActivationKeyDown({
          event,
          onActivate: activatesOnKeyboard
            ? model.clickInvestigationHandlers.containerOnClick
            : undefined,
        });
      }}
      style={model.buttonStyle}
      onTouchStart={model.pressState.handleTouchStart}
      onTouchEnd={model.pressState.handleTouchEnd}
      onTouchCancel={model.pressState.handleTouchCancel}
      onMouseEnter={model.pressState.handleMouseEnter}
      onMouseLeave={model.pressState.handleMouseLeave}
      onMouseDown={model.pressState.handleMouseDown}
      onMouseUp={model.pressState.handleMouseUp}
      onClickCapture={
        model.clickInvestigationHandlers.handleClickInvestigationCapture
      }
      onClick={
        rootStyle || rootClassName
          ? (event) => {
              if (event.target === event.currentTarget) {
                model.clickInvestigationHandlers.containerOnClick?.(event);
              }
            }
          : undefined
      }
      onPointerDown={onPointerDown}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
      onPointerDownCapture={
        model.clickInvestigationHandlers.handlePointerDownInvestigationCapture
      }
    >
      {containerElement}
    </div>
  );
}
